/**
 * View component for /admin/content-management/courses
 *
 * Generic course list view. Defaults to 'all' with:
 * this.props.dispatch(courseActions.fetchListIfNeeded());
 *
 * NOTE: See /product/views/ProductList.js.jsx for more examples
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

// import actions
import * as courseActions from '../courseActions';
import * as sponsorActions from '../../sponsor/sponsorActions';
import * as studyActions from '../../study/studyActions';

// import global components
import TrainingManagementLayout from '../../admin/components/TrainingManagementLayout.js.jsx';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from '../../../global/components/routing/Breadcrumbs.js.jsx';
import Pagination from "../../../global/components/helpers/Pagination.js.jsx";
import { SearchInput } from '../../../global/components/forms';

// import utilities
import filterUtils from '../../../global/utils/filterUtils';

// import course components
import AdminCourseListItem from '../components/AdminCourseListItem.js.jsx';

// import course css modules
import courseStyles from '../courseModuleStyles.css';

class AdminCourseList extends Base {
  constructor(props) {
    super(props);
    this.state = {
      perPage: 50
      , queryText: this.props.query || ''
    };
    this._bind(
      '_handleQuery'
      , '_handleSetPagination'
      , '_handleSort'
      , '_resetPagination'
      , '_setPerPage'
    );
  }

  componentDidMount() {
    const { dispatch } = this.props;
    // fetch a list of your choice
    dispatch(courseActions.fetchListIfNeeded("all")); // defaults to 'all'
    dispatch(courseActions.setFilter({query: '', sortBy: 'name'}));
    dispatch(courseActions.setPagination({page: 1, per: 50}));
    dispatch(sponsorActions.fetchListIfNeeded('all'));
    dispatch(studyActions.fetchListIfNeeded('all')); // defaults to 'all'
  }

  _handleSort(sortBy) {
    const { dispatch, match, courseList } = this.props;
    let newFilter = courseList.filter;
    if(courseList.filter.sortBy && courseList.filter.sortBy.indexOf("-") < 0) {
      sortBy = "-" + sortBy;
    } else {
      sortBy = sortBy.substring(0)
    }
    newFilter.sortBy = sortBy;
    dispatch(courseActions.setFilter(newFilter));
  }

  _resetPagination() {
    const { dispatch, match } = this.props;
    dispatch(courseActions.setPagination({
      page: 1,
      per: parseInt(this.state.perPage)
    }));
  }

  _setPerPage(per) {
    const { dispatch, match, courseList } = this.props;
    let newPagination = courseList.pagination;
    newPagination.per = parseInt(per);
    newPagination.page = 1;
    dispatch(courseActions.setPagination(newPagination));
    this.setState({perPage: per});
  }

  _handleSetPagination(newPagination) {
    const { dispatch, match } = this.props;
    dispatch(courseActions.setPagination(newPagination));
  }

  _handleQuery(e) {
    const { dispatch, match, courseList } = this.props;
    // always defaulting the page to page 1 so we can see our results
    let pagination = {};
    pagination.page = 1;
    pagination.per = courseList.pagination.per;
    this._handleSetPagination(pagination);
    // continue query logic
    dispatch(courseActions.setFilter({
      query: e.target.value.toLowerCase()
      , sortBy: courseList.filter.sortBy
    }));
  }


  render() {
    const {
      courseList
      , courseStore
      , location
      , paginatedList
      , sortedList
      , sponsorList
      , sponsorMap
      , studyList
      , studyMap
    } = this.props;

    /**
     * Retrieve the list information and the list items for the component here.
     *
     * NOTE: if the list is deeply nested and/or filtered, you'll want to handle
     * these steps within the mapStoreToProps method prior to delivering the
     * props to the component.  Othwerwise, the render() action gets convoluted
     * and potentially severely bogged down.
     */

    const isEmpty = (
      !courseList
      || !courseList.items
      || courseList.didInvalidate
      || !sponsorList
      || !studyList
    );
    const sortedEmpty = sortedList.length === 0;

    return (
      <TrainingManagementLayout>
        <div className="flex">
          <section className="section">
            <div className="yt-container">
              <Breadcrumbs links={location.state.breadcrumbs} />
              <h1> Courses</h1>
              <hr/>
              { isEmpty ?
                (courseList && courseList.isFetching ? <h2>Loading...</h2> : <h2>Empty.</h2>)
                :
                <div style={{ opacity: courseList.isFetching ? 0.5 : 1 }}>
                  <div className="yt-toolbar">
                    <div className="yt-tools right">
                      <div className="search">
                        <SearchInput
                          name="query"
                          value={courseList.filter.query}
                          change={this._handleQuery}
                          placeholder="Search..."
                          required={false}
                          />
                      </div>
                    </div>
                  </div>
                  <div className="content-container">
                    <div className="table-wrapper">
                      <table className="yt-table striped">
                        <caption>
                          { courseList.filter && courseList.filter.query && courseList.filter.query.length > 0 ?
                            <span>Filtered Studies &mdash; {sortedList.length} of {Object.keys(courseList.items).length}</span>
                            :
                            <span>All Studies &mdash; {Object.keys(courseList.items).length}</span>
                          }
                          <div className="per-page-select u-pullRight">
                            <label>Show per page: </label>
                            <select
                              name="numPerPage"
                              onChange={(e) => this._setPerPage(e.target.value)}
                              value={this.state.perPage}
                              >
                              <option value={25}> 25 </option>
                              <option value={50}> 50 </option>
                              <option value={100}> 100 </option>
                            </select>
                          </div>
                        </caption>
                        <thead>
                          <tr>
                            <th className="_40 sortable" onClick={()=> this._handleSort('title')}>
                              Title
                              {courseList.filter.sortBy === '-title' ?
                                <i className="ion ion-ios-arrow-down" />
                                : courseList.filter.sortBy === 'title' ?
                                <i className="ion ion-ios-arrow-up" />
                                : null
                              }
                            </th>
                            <th className="_20 sortable" onClick={() => this._handleSort('sponsorName')}>
                              Sponsor
                              {courseList.filter.sortBy === '-sponsorName' ?
                                <i className="ion ion-ios-arrow-down" />
                                : courseList.filter.sortBy === 'sponsorName' ?
                                <i className="ion ion-ios-arrow-up" />
                                : null
                              }
                            </th>
                            <th className="_20 sortable" onClick={() => this._handleSort('studyName')}>
                              Study
                              {courseList.filter.sortBy === '-studyName' ?
                                <i className="ion ion-ios-arrow-down" />
                                : courseList.filter.sortBy === 'studyName' ?
                                <i className="ion ion-ios-arrow-up" />
                                : null
                              }
                            </th>
                            <th className="_20 sortable" onClick={()=> this._handleSort('updated')}>
                              Last Modified
                              {courseList.filter.sortBy === 'updated' ?
                                <i className="ion ion-ios-arrow-up" />
                                : courseList.filter.sortBy === '-updated' ?
                                <i className="ion ion-ios-arrow-down" />
                                : null
                              }
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {
                            paginatedList.map((course, i) =>
                            <AdminCourseListItem
                              key={course._id}
                              course={course}
                              sponsor={sponsorMap[course._organization]}
                              study={studyMap[course._group]}
                            />
                          )
                        }
                      </tbody>
                    </table>
                    { sortedEmpty ?
                      null
                      :
                      <Pagination
                        pagination={courseList.pagination}
                        setPagination={this._handleSetPagination}
                        totalPages={ Math.floor(sortedList.length / courseList.pagination.per) + 1}
                      />
                    }
                  </div>
                </div>
                </div>
              }
            </div>
          </section>
        </div>
      </TrainingManagementLayout>
    )
  }
}

AdminCourseList.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  const courseStore = store.course;
  const courseList = courseStore.lists ? courseStore.lists.all : null;
  const sponsorList = store.sponsor.util.getList() || [];
  const studyList = store.study.util.getList() || [];
  let sortedList = [];
  let paginatedList = [];
  let filteredByQuery = [];
  if (courseList) {

    const pagination = courseList.pagination;
    const filter = courseList.filter;
    const query = filter ? filter.query : '';
    const sortBy = filter ? filter.sortBy : 'email';

    // FILTER BY QUERY
    let queryTestString = ("" + query).toLowerCase().trim();
    // queryTestString = queryTestString.replace(/[^a-zA-Z0-9]/g,''); // replace all non-characters and numbers
    filteredByQuery = courseList && courseList.items ? courseList.items.filter((courseId) => {
      return filterUtils.filterCourse(queryTestString, courseStore.byId[courseId], sponsorList, studyList);
    }) : []

    // SORT THE LIST
    sortedList = filteredByQuery.map((item) => {
      let newItem = courseStore.byId[item];
      newItem.sponsorName = store.sponsor.byId[newItem._organization] ? store.sponsor.byId[newItem._organization].name : '';
      newItem.studyName = store.study.byId[newItem._group] ? store.study.byId[newItem._group].name : '';
      return newItem;
    });
    switch(sortBy) {
      case 'title':
        sortedList = _.orderBy(sortedList, [item => item.title.toLowerCase()], ['asc']);
        break;
      case '-title':
        sortedList = _.orderBy(sortedList, [item => item.title.toLowerCase()], ['desc']);
        break;
      case 'sponsorName':
        sortedList = _.orderBy(sortedList, [item => item.sponsorName.toLowerCase()], ['asc']);
        break;
      case '-sponsorName':
        sortedList = _.orderBy(sortedList, [item => item.sponsorName.toLowerCase()], ['desc']);
        break;
      case 'studyName':
        sortedList = _.orderBy(sortedList, [item => item.studyName.toLowerCase()], ['asc']);
        break;
      case '-studyName':
        sortedList = _.orderBy(sortedList, [item => item.studyName.toLowerCase()], ['desc']);
        break;
      case 'updated':
        sortedList = _.orderBy(sortedList, [item => item.updated], ['asc']);
        break;
      case '-updated':
        sortedList = _.orderBy(sortedList, [item => item.updated], ['desc']);
        break;
      default:
        sortedList = _.orderBy(sortedList, [item => item.title.toLowerCase()], ['asc']);
    }

    // apply pagination
    let start = pagination ? (pagination.page - 1) * pagination.per : 1;
    let end = pagination ? start + pagination.per : 0;
    paginatedList = _.slice(sortedList, start, end);

  }
  return {
    courseList
    , courseStore
    , paginatedList
    , sortedList
    , sponsorList
    , sponsorMap: store.sponsor.byId
    , studyList
    , studyMap: store.study.byId
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminCourseList)
);



// WEBPACK FOOTER //
// modules/course/views/AdminCourseList.js.jsx